import { ASSIGNMENT } from "../../models/assignment";
import { ASSIGNMENT_SCHEDULE } from 'sh-www/models/assignment-schedule';
import { ASSIGNMENT_COMMAND_CONSTANTS } from "../../services/commands/assignment-commands";
import { SH_ERROR_INDEX } from 'sh-www/utils/adapter-errors';

/**
 * The posting type names keyed by the numerical value.
 *
 * @type {Object}
 */
export const POSTING_CREATE_TYPE_NAMES = {
  [ASSIGNMENT.TYPES.STANDARD]: 'find-freelancer',
  [ASSIGNMENT.TYPES.PITCH]: 'pitch',
};

/**
 * Includes for the assignment that are shared across the posting-create/edit experience.
 *
 * @type {string[]}
 */
export const POSTING_CREATE__ASSIGNMENT_INCLUDES = [
  'classifications',
  'assignment-deliverables',
  'assignment-schedules',
  'candidate-location',
  'location',
  'services',
  'followers',
  'buyer',
  'buyer.company',
  'company-team',
  'production',
];

export const POSTING_CREATE__CLASSIFICATION_CATEGORY_LIMIT = 3

/**
 * The enums for the possible cover expenses value.
 *
 * @type {Object}
 */
export const POSTING_CREATE_COVER_EXPENSES_TYPE = {
  YES: 'yes',
  NO: 'no'
};

/**
 * The enums for the possible cover fees value.
 *
 * @type {Object}
 */
export const POSTING_CREATE_COVER_FEES_TYPE = {
  YES: true,
  NO: false,
};

/**
 * The enums for the possible cover fees value.
 *
 * @type {Object}
 */
export const POSTING_CREATE_EXPEDITE_TYPE = {
  YES: true,
  NO: false,
};

/**
 * These are the enums for the possible date ranges that the user can select for the posting.
 *
 * @type {Object}
 */
export const POSTING_CREATE_DATE_RANGE_TYPE = {
  WITHIN_24_HOURS : 'within_24',
  SELECT_A_DATE : 'select_date'
};

/**
 * @type {number}     Minimum number of days for a project
 */
export const POSTING_CREATE_MIN_WHOLE_DAY_COUNT = 1;

/**
 * @type {number}     Number of whole day buttons for the estimated days we will have
 */
export const POSTING_CREATE_ESTIMATED_WHOLE_DAY_COUNT = 3;

/**
 * @type {Object}     Enums for the button types for the estimated days
 */
export const POSTING_CREATE_ESTIMATED_DAY_TYPES = {
  CUSTOM_NUMBER_OF_DAYS: 'custom_days',
  WHOLE_DAY: 'whole_day_' //This is dynamic, whole days will be like whole_day_[1|2|3|...]
};

/**
 * Error codes for the posting creation route.
 *
 * @type {Object}
 */
export const POSTING_CREATE__ERROR_CODES = {
  ERROR_NEEDS_TEST_MATCHES_CONFIRM: 'need-test-matches-confirm'
};

/**
 * The enum for the types of freelancer levels we support.
 *
 * @type {Object}
 */
export const POSTING_CREATE_FREELANCER_LEVEL = {
  BASIC: 'basic',
  PREMIUM: 'premium',
  STANDARD: 'standard'
};

/**
 * Constants for the widget types for the testMatches feedback.
 *
 * @type {Object}
 */
export const POSTING_CREATE_HELP_WIDGET_TYPE = {
  BUDGET: 'budget',
  RATING: 'rating'
};

/**
 * These are the different location types that the user could choose.
 *
 * @type {Object}
 */
export const POSTING_CREATE_LOCATION_TYPE = {
  GLOBAL : 'location_type_global',
  COUNTRY : 'location_type_countries',
  CITY : 'location_type_city'
};

/**
 * This is a map of the Posting privacy level to the title for the dropdown in assignment creation.
 *
 * @type {Object}
 */
export const POSTING_CREATE_PRIVACY_LEVEL_TO_TITLES_MAP = {
  [ASSIGNMENT.PRIVACY_LEVEL.PUBLIC] : 'Anyone on Glimmer',
  [ASSIGNMENT.PRIVACY_LEVEL.NETWORK] : 'Only My Network',
  [ASSIGNMENT.PRIVACY_LEVEL.GROUP] : 'Only My Network',
  [ASSIGNMENT.PRIVACY_LEVEL.PRIVATE] : 'Unlisted'
};

export const POSTING_CREATE_SCHEDULE_OPTION_LABELS = {
  [ASSIGNMENT_SCHEDULE.TYPES.ESTIMATED_SHOOT_DATE]: 'Estimated shoot date',
  [ASSIGNMENT_SCHEDULE.TYPES.EXACT_SHOOT_DATE]: 'Exact shoot date',
  [ASSIGNMENT_SCHEDULE.TYPES.COMPLETION_DEADLINE]: 'Completion deadline',
  [ASSIGNMENT_SCHEDULE.TYPES.KEY_MEETING]: 'Key meeting',
  [ASSIGNMENT_SCHEDULE.TYPES.OTHER_TYPE]: 'Other...',
};

/**
 * Querey params for the posting create experience
 *
 * @type {Object}
 */
export const POSTING_CREATE__QUERY_PARAMS = {
  CLONE_ID: 'clone_id',
  SERVICE_IDS: 'service_ids',
  CLASSIFICATION_IDS: 'classification_ids',
  LOCATION_ID: 'location_id',
  MAX_RATE: 'max_rate',
  RATE_TYPE: 'rate_type',
};

/**
 * Map of the assignment day rate enum to the human readable title for the rate types.
 *
 * @type {Object}
 */
export const POSTING_CREATE_RATE_TYPE_TO_TITLES_MAP = {
  [ASSIGNMENT.RATE_TYPE.FLAT] : 'Flat rate',
  [ASSIGNMENT.RATE_TYPE.DAY] : 'Day rate'
};

/**
 * The enums for the possible cover expenses value.
 *
 * @type {Object}
 */
export const POSTING_CREATE_USE_KEY_DATES = {
  YES: true,
  NO: false,
};

/**
 * The allowed markup tags for the free text in the tiny mce editors
 *
 * **** NOTE - If you update this, you need to update what is allowed in the tiny-mce editors ****
 *
 * @type {string[]}
 */
export const POSTING_CREATE_VALID_FREE_TEXT_HTML = ['a', 'strong', 'b', 'br', 'p', 'ul', 'ol', 'li', 'i', 'u', 'em', 'span'];

export const KEY_DATES_OPTIONS = [
  {
    key: POSTING_CREATE_SCHEDULE_OPTION_LABELS[ASSIGNMENT_SCHEDULE.TYPES.ESTIMATED_SHOOT_DATE],
    model: ASSIGNMENT_SCHEDULE.TYPES.ESTIMATED_SHOOT_DATE,
  },
  {
    key: POSTING_CREATE_SCHEDULE_OPTION_LABELS[ASSIGNMENT_SCHEDULE.TYPES.EXACT_SHOOT_DATE],
    model: ASSIGNMENT_SCHEDULE.TYPES.EXACT_SHOOT_DATE,
  },
  {
    key: POSTING_CREATE_SCHEDULE_OPTION_LABELS[ASSIGNMENT_SCHEDULE.TYPES.COMPLETION_DEADLINE],
    model: ASSIGNMENT_SCHEDULE.TYPES.COMPLETION_DEADLINE,
    limit: 1,
  },
  {
    key: POSTING_CREATE_SCHEDULE_OPTION_LABELS[ASSIGNMENT_SCHEDULE.TYPES.KEY_MEETING],
    model: ASSIGNMENT_SCHEDULE.TYPES.KEY_MEETING,
  },
  {
    key: POSTING_CREATE_SCHEDULE_OPTION_LABELS[ASSIGNMENT_SCHEDULE.TYPES.OTHER_TYPE],
    model: ASSIGNMENT_SCHEDULE.TYPES.OTHER_TYPE,
    custom: true,
  },
];
